/**
 * @sazeo/sdk — Audit Log hooks
 *
 * TanStack Query wrapper for the tenant audit log.
 * Admin/owner only; enforced server-side.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuditLogEntry {
  id: string;
  tenant_id: string;
  actor_user_id: string | null;
  actor_email?: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  metadata: Record<string, unknown> | null;
  ts: string;
}

export interface AuditLogFilters {
  action?: string;
  actor_id?: string;
  from?: string;
  to?: string;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const auditLogKeys = {
  all: ['audit-log'] as const,
  list: (tenantId: string, filters: AuditLogFilters) =>
    [...auditLogKeys.all, tenantId, filters] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useAuditLog — GET /functions/v1/app/audit
 * Returns audit log entries for the tenant.
 * Admin/owner only; enforced server-side.
 */
export function useAuditLog(
  supabase: SupabaseClientLike,
  tenantId: string,
  filters: AuditLogFilters = {},
): UseQueryResult<AuditLogEntry[]> {
  return useQuery({
    queryKey: auditLogKeys.list(tenantId, filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.action) params.set('action', filters.action);
      if (filters.actor_id) params.set('actor_id', filters.actor_id);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      params.set('limit', String(filters.limit ?? 200));
      const qs = params.toString();
      return apiFetch<AuditLogEntry[]>(supabase, `app/audit${qs ? `?${qs}` : ''}`);
    },
    enabled: !!tenantId,
  });
}
